// OpenAI Ads Pixel tracking helpers.
// Base pixel is initialized ONCE in /public/index.html — do not re-init here.
// Reference: https://bzrcdn.openai.com/sdk/oaiq.min.js

const _fired = new Set(); // simple in-session dedup keys
const _lastAt = new Map(); // last-fired timestamp per key (for click dedup)

function _oaiq(...args) {
  if (typeof window === "undefined" || typeof window.oaiq !== "function") return;
  try {
    window.oaiq(...args);
  } catch (e) {
    // never throw from tracking
  }
}

// Normalize and SHA-256 hash a value (email/phone/id) for OpenAI user matching.
export async function sha256(value) {
  if (!value || typeof window === "undefined" || !window.crypto?.subtle) return "";
  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, "");
  const bytes = new TextEncoder().encode(normalized);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Fire once per unique key (dedup across React Strict Mode double-mounts, re-renders).
function _fireOnce(key, run) {
  if (_fired.has(key)) return;
  _fired.add(key);
  run();
}

// Rate-limited fire (dedupe repeated click bubbles within 400ms).
function _fireDebounced(key, run, windowMs = 400) {
  const now = Date.now();
  const last = _lastAt.get(key) || 0;
  if (now - last < windowMs) return;
  _lastAt.set(key, now);
  run();
}

// --- Standard events -------------------------------------------------------

export function trackPageView(pathname) {
  const key = `pv:${pathname}`;
  _fireOnce(key, () => _oaiq("measure", "page_viewed", { type: "contents" }));
}

export function trackContentsView(pageKey) {
  const key = `cv:${pageKey}`;
  _fireOnce(key, () => _oaiq("measure", "contents_viewed", { type: "contents" }));
}

export async function trackLeadCreated({ email, phone, externalId }) {
  const [em, ph, ex] = await Promise.all([sha256(email), sha256(phone), sha256(externalId)]);
  const userData = {};
  if (em) userData.email_sha256 = em;
  if (ph) userData.phone_number_sha256 = ph;
  if (ex) userData.external_id_sha256 = ex;
  // 4th arg = user matching params (per OpenAI SDK convention)
  _oaiq("measure", "lead_created", { type: "customer_action" }, userData);
}

export function trackAppointmentScheduled() {
  _fireDebounced("appointment_scheduled", () =>
    _oaiq("measure", "appointment_scheduled", { type: "customer_action" })
  );
}

// --- Custom events ---------------------------------------------------------

function _trackCustom(customName) {
  _fireDebounced(`custom:${customName}`, () =>
    _oaiq(
      "measure",
      "custom",
      { type: "custom" },
      { custom_event_name: customName }
    )
  );
}

export function trackWhatsAppClick() {
  _trackCustom("whatsapp_clicked");
}

export function trackPhoneClick() {
  _trackCustom("phone_clicked");
}

export function trackEmailClick() {
  _trackCustom("email_clicked");
}

// Listen for Calendly's postMessage confirming a successful booking.
// Only registered once per page load.
let _calendlyBound = false;
export function bindCalendlyAppointmentListener() {
  if (_calendlyBound || typeof window === "undefined") return;
  _calendlyBound = true;
  window.addEventListener("message", (e) => {
    if (!e || typeof e.data !== "object" || e.data === null) return;
    const type = e.data.event;
    if (typeof type === "string" && type === "calendly.event_scheduled") {
      trackAppointmentScheduled();
    }
  });
}
